import React, { useCallback, useRef, useMemo, useEffect, useState } from 'react';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { DEFAULT_STATUSES } from '../FlowchartView/TaskNode/constants';
import { calculateLockStatesWithParentContext } from '../FlowchartView/utils/dependencyUtils';
import { useWaitGateNow } from '../shared/ConditionsDropdown/utils/useWaitGateNow';
import { subscribeMissionToSidebar } from '../../../utils/missionToSidebarBridge';
import KanbanColumn from './components/KanbanColumn';
import KanbanHeader from './components/KanbanHeader';
import KanbanBoard from './components/KanbanBoard';
import SwimlanesBoard from './components/SwimlanesBoard';
import { CardOverlay } from './components/Overlays';
import SwimlaneColumnDragOverlay from './components/SwimlaneColumnDragOverlay';
import FokusOverlay from '../shared/FokusOverlay';
import useKanbanClipboard from './hooks/useKanbanClipboard';
import useKanbanDragDrop from './hooks/useKanbanDragDrop';
import useKanbanScroll from './hooks/useKanbanScroll';
import useKanbanData from './hooks/useKanbanData';
import useKanbanSelection from './hooks/useKanbanSelection';
import useColumnCollapse from './hooks/useColumnCollapse';
import useSwimlaneTracking from './hooks/useSwimlaneTracking';
import useCardHandlerProps from './hooks/useCardHandlerProps';
import { DEFAULT_SWIMLANE_SETTING } from '../config/swimlaneConfig';
import './styles/index.css';

/**
 * KanbanView - Main Kanban board with drag-and-drop, swimlanes support
 */
const KanbanView = ({
  tasks = [],
  categories = [],
  allTags = [],
  statuses = DEFAULT_STATUSES,
  nestedSprints = [],
  campaignId = '',
  campaignName = '',
  campaignIcon = null,
  campaignProgress = 0,
  breadcrumbPath = [],
  onNavigateToParent,
  onSprintIconChange,
  onRenameSprint,
  kanbanColumnOrder = [],
  onStatusChange,
  onCategoryChange,
  onRenameCategory,
  onDeleteCategory,
  onAddTag,
  onRemoveTag,
  onSetPrimaryTag,
  onRenameTag,
  onDeleteTag,
  onOpenDetails,
  onOpenAttachments,
  onOpenNestedSprint,
  onCreateNestedSprint,
  onRemoveNestedSprint,
  onPasteTasks,
  onDeleteTask,
  onDeleteTasks,
  onDuplicateTask,
  onCreateParallelMission,
  onInsertMissionBefore,
  onColumnReorder,
  onAddTask,
  onAddDecision,
  onAddWait,
  onIconChange,
  onRename,
  onDueDateChange,
  onRemindersChange,
  onEstimatedDurationMinutesChange,
  onCreatedAtChange,
  onStartedAtChange,
  onCompletedAtChange,
  onUpdatedAtChange,
  onPriorityChange,
  onDifficultyChange,
  onCustomFieldValueChange,
  customFields = [],
  onRenameStatus,
  onDeleteStatus,
  onAddStatus,
  onReassignTasksStatus,
  onSetStatusAutoSync,
  onSetWipLimit,
  onDeliverablesChange,
  onInputsChange,
  onArchiveTask,
  onUnarchiveTask,
  onDependencyChange,
  onDecisionAnswerChange,
  onDecisionOptionsChange,
  editingColumnId,
  onFinishColumnEdit,
  cardDisplaySettings = {},
  swimlaneSetting = DEFAULT_SWIMLANE_SETTING,
  sortSetting = 'manual',
  filters = {},
  showArchived = false,
  showActionableOnly = false,
  isTemplate = false,
  // FOKUS mode props
  fokusMode = null, // { isFokusMode, fokusFilteredTasks, isPrimaryFokused, isDependencyTask }
  // View navigation (for "Open in view" button)
  currentView,
  customViews = [],
  activeCustomViewId = null,
  onViewChange,
  onSelectCustomView,
  onGoToTaskInView,
  // Deliverables warning modal
  onSubmitDeliverable,
  // Deliverables editing (for conditions modal)
  onEditDeliverables,
  onClearDeliverable,
  localSprint = null,
  allSprints = [],
  onSelectSprint,
}) => {
  const boardRef = useRef(null);
  const [hideCardOverlayForSidebar, setHideCardOverlayForSidebar] = useState(false);

  useEffect(() => {
    return subscribeMissionToSidebar((state) => {
      setHideCardOverlayForSidebar(state.active && state.isOverSidebar);
    });
  }, []);

  // Use FOKUS filtered tasks when in FOKUS mode
  const effectiveTasks = fokusMode?.isFokusMode ? fokusMode.fokusFilteredTasks : tasks;

  // Data hook
  const {
    columns, columnIds, tasksByStatus, taskIdsByColumn,
    completedCount, totalCount, remainingMinutes, remainingMinMinutes, missingEstCount, remainingBreakdown,
    swimlanes, hasSwimlanes,
    collapsedColumns, setCollapsedColumns, expandingColumns, setExpandingColumns,
  } = useKanbanData({
    tasks: effectiveTasks, statuses, kanbanColumnOrder, campaignId,
    allTags, nestedSprints, swimlaneSetting, sortSetting, filters, showArchived, showActionableOnly, customFields,
    localSprint,
    allSprints,
  });

  // Calculate lock states for dependency warnings (same logic as FlowchartView)
  const waitNow = useWaitGateNow(effectiveTasks);
  const lockStates = useMemo(() => {
    return calculateLockStatesWithParentContext(effectiveTasks, nestedSprints, statuses, {
      currentSprint: localSprint,
      allSprints,
      now: waitNow,
    });
  }, [effectiveTasks, nestedSprints, statuses, localSprint, allSprints, waitNow]);

  const onNavigateToParentMission = useCallback(
    ({ parentSprintId, parentMissionId }) => {
      const parentSprint = allSprints?.find((s) => s.id === parentSprintId);
      if (parentSprint && onSelectSprint) {
        onSelectSprint(parentSprint, { view: currentView || 'kanban', missionId: parentMissionId });
      }
    },
    [allSprints, onSelectSprint, currentView],
  );

  // Selection hook
  const { selectedTaskIds, setSelectedTaskIds, handleTaskClick, handleBoardClick } = useKanbanSelection();
  
  // Dispatch selection change event for FOKUS mode integration
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('kanbanSelectionChange', {
      detail: { selectedTaskIds, view: 'kanban' }
    }));
  }, [selectedTaskIds]);

  // Column collapse hook
  const { handleToggleCollapse } = useColumnCollapse({ campaignId, setCollapsedColumns, setExpandingColumns });

  // Swimlane tracking hook
  const { currentSwimlaneId, swimlaneRowRefs } = useSwimlaneTracking({ hasSwimlanes, swimlanes, boardRef });

  // Scroll to task helper (for paste navigation)
  const scrollToTask = useCallback((taskId) => {
    const cardElement = document.querySelector(`[data-task-id="${taskId}"]`);
    if (cardElement) {
      // Use instant scroll so element is immediately visible
      cardElement.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'center' });
      
      // Flash highlight immediately after instant scroll
      // Duration matches the CSS animation (1.8s pulse + fade)
      cardElement.classList.add('kanban-card--highlighted');
      setTimeout(() => {
        cardElement.classList.remove('kanban-card--highlighted');
      }, 2000);
    }
  }, []);
      
  // Clipboard hook
  const { copyCard, duplicateCard } = useKanbanClipboard({
    tasks, selectedTaskIds, setSelectedTaskIds, nestedSprints, allSprints, allTags, customFields,
    onPasteTasks, onDeleteTasks, scrollToTask,
  });

  // WIP limit blocked handler - show toast notification
  const handleWipLimitBlocked = useCallback((reason) => {
    // TODO: Replace with a proper toast notification system
    console.warn('WIP Limit:', reason);
  }, []);

  // Drag-and-drop hook
  const {
    activeColumnId, activeCardId, activeCardSwimlaneId, frozenTaskCounts, frozenHiddenCounts, visibleCardCount,
    sensors, collisionDetection, autoScrollConfig, handleDragStart, handleDragMove, handleDragOver, handleDragEnd,
    adjustColumnOverlay, adjustCardForSidebar,
  } = useKanbanDragDrop({
    columns, columnIds, taskIdsByColumn, tasksByStatus, 
    tasks, statuses, // For WIP limit checking
    onStatusChange, onSetStatusAutoSync, onColumnReorder, 
    onWipLimitBlocked: handleWipLimitBlocked,
    boardRef,
    hasSwimlanes, swimlanes, // Pass swimlane info for restricting card drops
    campaignId,
  });

  // Scroll hook
  const { handleBoardWheel, handleViewWheel } = useKanbanScroll({ boardRef, scrollSpeed: 1.5 });

  // Card handler props hook
  const { cardHandlerProps, editingCardId, setEditingCardId } = useCardHandlerProps({
    categories, allTags, allTasks: tasks, nestedSprints, allSprints, statuses, cardDisplaySettings,
    lockStates, isTemplate,
    handleTaskClick, onOpenDetails, onOpenAttachments, onCategoryChange,
    onRenameCategory, onDeleteCategory, onAddTag, onRemoveTag,
    onSetPrimaryTag, onRenameTag, onDeleteTag, onOpenNestedSprint,
    onCreateNestedSprint, onRemoveNestedSprint, onIconChange, onRename,
    onStatusChange, onDueDateChange, onRemindersChange, onEstimatedDurationMinutesChange,
    onCreatedAtChange, onStartedAtChange, onCompletedAtChange,
    onUpdatedAtChange, onPriorityChange, onDifficultyChange, onCustomFieldValueChange, customFields, onDeleteTask, onDuplicateTask, onCreateParallelMission, onInsertMissionBefore, onSetStatusAutoSync,
    onDeliverablesChange, onInputsChange, onArchiveTask, onUnarchiveTask, onDependencyChange,
    onDecisionAnswerChange, onDecisionOptionsChange,
    copyCard, duplicateCard, onAddStatus, onDeleteStatus, onRenameStatus,
    currentView, customViews, activeCustomViewId, onViewChange, onSelectCustomView, onGoToTaskInView,
    onSubmitDeliverable, onEditDeliverables, onClearDeliverable,
    onNavigateToParentMission,
  });

  // Handlers
  const handleColumnNameSave = useCallback((columnId, newName) => {
    if (newName.trim()) onRenameStatus?.(columnId, newName.trim());
    onFinishColumnEdit?.();
  }, [onRenameStatus, onFinishColumnEdit]);

  // Drag overlay data
  const activeColumn = activeColumnId ? columns.find(c => c.id === activeColumnId) : null;
  const activeCard = activeCardId ? tasks.find(t => t.id === activeCardId) : null;
  const activeCardCategory = activeCard?.category ? categories.find(c => c.name === activeCard.category) : null;

  const viewClassName = `kanban-view${fokusMode?.isFokusMode ? ' kanban-view--fokus-mode' : ''}`;

  return (
    <div className={viewClassName} onWheel={hasSwimlanes ? undefined : handleViewWheel}>
      {/* FOKUS mode overlay - dithered blue vignette */}
      {/* skipEnterAnimation=true because if we're rendering, fokus was already active */}
      {fokusMode?.isFokusMode && <FokusOverlay skipEnterAnimation />}
      
      <KanbanHeader
        campaignName={campaignName}
        campaignIcon={campaignIcon}
        campaignProgress={campaignProgress}
        breadcrumbPath={breadcrumbPath}
        completedCount={completedCount}
        totalCount={totalCount}
        remainingMinutes={remainingMinutes}
        remainingMinMinutes={remainingMinMinutes}
        missingEstCount={missingEstCount}
        remainingBreakdown={remainingBreakdown}
        columns={columns}
        tasksByStatus={tasksByStatus}
        onNavigateToParent={onNavigateToParent}
        onIconChange={onSprintIconChange}
        onRename={onRenameSprint}
      />

      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        autoScroll={autoScrollConfig}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        {hasSwimlanes ? (
          <SwimlanesBoard
            ref={boardRef}
            columns={columns}
            columnIds={columnIds}
            tasksByStatus={tasksByStatus}
            swimlanes={swimlanes}
            sortSetting={sortSetting}
            collapsedColumns={collapsedColumns}
            expandingColumns={expandingColumns}
            selectedTaskIds={selectedTaskIds}
            activeColumnId={activeColumnId}
            activeCardId={activeCardId}
            activeCardSwimlaneId={activeCardSwimlaneId}
            currentSwimlaneId={currentSwimlaneId}
            swimlaneRowRefs={swimlaneRowRefs}
            swimlaneSetting={swimlaneSetting}
            statuses={statuses}
            cardHandlerProps={cardHandlerProps}
            editingCardId={editingCardId}
            setEditingCardId={setEditingCardId}
            onToggleCollapse={handleToggleCollapse}
            onSetWipLimit={onSetWipLimit}
            onDeleteColumn={onDeleteStatus}
            onAddStatus={onAddStatus}
            onReassignTasksStatus={onReassignTasksStatus}
            onAddTask={onAddTask}
            onAddDecision={onAddDecision}
            onAddWait={onAddWait}
            onRenameStatus={onRenameStatus}
            onBoardClick={handleBoardClick}
          />
        ) : (
          <KanbanBoard
              ref={boardRef}
            columns={columns}
            columnIds={columnIds}
            tasksByStatus={tasksByStatus}
            taskIdsByColumn={taskIdsByColumn}
                    selectedTaskIds={selectedTaskIds}
            collapsedColumns={collapsedColumns}
            expandingColumns={expandingColumns}
            editingColumnId={editingColumnId}
            activeColumnId={activeColumnId}
                    activeCardId={activeCardId}
            frozenTaskCounts={frozenTaskCounts}
            frozenHiddenCounts={frozenHiddenCounts}
            visibleCardCount={visibleCardCount}
            cardHandlerProps={cardHandlerProps}
                    onToggleCollapse={handleToggleCollapse}
                    onAddTask={onAddTask}
                    onAddDecision={onAddDecision}
                    onAddWait={onAddWait}
                    onColumnNameSave={handleColumnNameSave}
                    onDeleteColumn={onDeleteStatus}
                    onAddStatus={onAddStatus}
                    onReassignTasksStatus={onReassignTasksStatus}
            onSetWipLimit={onSetWipLimit}
            onBoardClick={handleBoardClick}
            onBoardWheel={handleBoardWheel}
                  />
        )}

        <DragOverlay 
          dropAnimation={activeColumn ? null : { duration: 200, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)' }}
          modifiers={activeColumn ? [adjustColumnOverlay] : [adjustCardForSidebar]}
        >
          {activeColumn ? (
            hasSwimlanes ? (
              collapsedColumns.has(activeColumn.id) ? (
                // Collapsed column overlay - simple narrow bar
                <div className="swimlane-collapsed-drag-overlay">
                  <div className="swimlane-collapsed-drag-overlay__header">
                    <span className="kanban-swimlanes__header-drag-handle">
                      <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor">
                        <circle cx="2" cy="2" r="1.5" />
                        <circle cx="8" cy="2" r="1.5" />
                        <circle cx="2" cy="7" r="1.5" />
                        <circle cx="8" cy="7" r="1.5" />
                        <circle cx="2" cy="12" r="1.5" />
                        <circle cx="8" cy="12" r="1.5" />
                      </svg>
                    </span>
                  </div>
                  <div className="swimlane-collapsed-drag-overlay__body">
                    <span 
                      className="kanban-swimlanes__header-color" 
                      style={{ backgroundColor: activeColumn.color }} 
                    />
                    <span className="kanban-swimlanes__header-label">{activeColumn.name}</span>
                    <span className="kanban-swimlanes__header-count">
                      {tasksByStatus[activeColumn.id]?.length ?? 0}
                    </span>
                  </div>
                </div>
              ) : (
              <div className="swimlane-column-drag-wrapper">
                <SwimlaneColumnDragOverlay 
                  column={activeColumn} 
                  swimlanes={swimlanes}
                  tasksByStatus={tasksByStatus}
                  frozenTaskCount={frozenTaskCounts?.[activeColumn.id]}
                  categories={categories}
                  allTags={allTags}
                  nestedSprints={nestedSprints}
                  statuses={statuses}
                  cardDisplaySettings={cardDisplaySettings}
                  sortSetting={sortSetting}
                  maxSwimlanes={4}
                  maxCardsPerSwimlane={3}
                />
              </div>
              )
            ) : (
              <div className="kanban-column-drag-wrapper">
                <KanbanColumn
                  column={activeColumn}
                  tasks={(tasksByStatus[activeColumn.id] || []).slice(0, visibleCardCount)}
                  taskIds={(taskIdsByColumn[activeColumn.id] || []).slice(0, visibleCardCount)}
                  selectedTaskIds={selectedTaskIds}
                  isCollapsed={collapsedColumns.has(activeColumn.id)}
                  isDragOverlay={true}
                  frozenTaskCount={frozenTaskCounts?.[activeColumn.id]}
                  {...cardHandlerProps}
                />
              </div>
            )
          ) : activeCard && !hideCardOverlayForSidebar ? (
            <CardOverlay 
              card={activeCard} 
              category={activeCardCategory}
              statuses={statuses}
              allTags={allTags}
              nestedSprint={activeCard.nestedSprintId ? nestedSprints.find(s => s.id === activeCard.nestedSprintId) : null}
              cardDisplaySettings={cardDisplaySettings}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default KanbanView;
           